import React from 'react';
import { useAppContext, useOnboardingContext } from 'hooks';
import { useOpenInAppModal } from 'hooks/useOpenInAppModal';
import SmartAppBanner from 'components/common3/SmartAppBanner/SmartAppBanner';
import OpenInAppModal, {
  OpenInAppModalVariant,
} from 'components/common3/OpenInAppModal/OpenInAppModal';

export type AppBannerAndModalProps = {
  /** When true, the modal may auto-show (after delay); banner always shows on mobile when this component is mounted. */
  when: boolean;
  variant?: OpenInAppModalVariant;
  username?: string;
  profileImageUrl?: string | null;
  /** When true (e.g. on live stream), "Open App" only redirects to store. */
  redirectToStoreOnly?: boolean;
};

/**
 * Renders Smart App Banner + Open In App modal. Use on any page that should promote the app.
 * Encapsulates useOpenInAppModal so hooks are stable (no "more hooks" error when used after conditional return).
 */
const AppBannerAndModal = ({
  when,
  variant = 'profile',
  username,
  profileImageUrl,
  redirectToStoreOnly = false,
}: AppBannerAndModalProps): JSX.Element => {
  const { showSignUpModal, showLoginModal } = useAppContext();
  const { showOnboardingModal, showCreatorSetupModal } = useOnboardingContext();
  // Never stack the promo modal over a full-screen account flow, e.g. the
  // onboarding modal reopening on /dashboard after a social OAuth redirect.
  const isAccountFlowOpen = Boolean(
    showSignUpModal ||
      showLoginModal ||
      showOnboardingModal ||
      showCreatorSetupModal
  );

  const {
    isOpen: isOpenInAppModalOpen,
    close: closeOpenInAppModal,
    dismissStayOnWeb: dismissStayOnWebModal,
  } = useOpenInAppModal({
    mobileOnly: true,
    when: when && !isAccountFlowOpen,
  });

  return (
    <>
      <SmartAppBanner />
      <OpenInAppModal
        open={isOpenInAppModalOpen && !isAccountFlowOpen}
        onClose={closeOpenInAppModal}
        onStayOnWeb={dismissStayOnWebModal}
        variant={variant}
        username={username}
        profileImageUrl={profileImageUrl ?? undefined}
        redirectToStoreOnly={redirectToStoreOnly}
      />
    </>
  );
};

export default AppBannerAndModal;
